import { useState } from 'react'
import { X } from 'lucide-react'
import {
  DESCRIERE_ROL_PARTENER,
  ETICHETE_ROL_PARTENER,
  ROLURI_PARTENER,
  registruPentruRol,
  type RolPartener,
} from '@b4l/shared'
import { CautaAdaugaPartener } from '../../parteneri/CautaAdaugaPartener'

/** Un partener ales în formular, înainte de salvare. `partenerId` e din tabela comună. */
export interface PartenerAles {
  partenerId: number
  nume: string
  cif: string | null
  rol: RolPartener
  nota: string
}

/** Grupul unui beneficiar creat de aici: implicit „partener", fără agent. */
const GRUPURI_BENEFICIAR = [
  { value: 'partener', label: 'Partener (nu e client)' },
  { value: 'B2B', label: 'B2B' },
  { value: 'B2C', label: 'B2C' },
]

const intrare =
  'h-9 rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 text-corp text-b4l-text'

/**
 * Partenerii unei activități: unul sau mai mulți, fiecare cu rolul lui (Madalin, 15.10.2026:
 * „să-l poți selecta sau să-l creezi pe loc").
 *
 * Întâi rolul, apoi partenerul — cu câmpul comun „Caută / adaugă partener" (CRM, apoi
 * ExpertAccounts, apoi ANAF). Căutarea e în toată lista de parteneri, clienți și furnizori
 * deodată: omul știe pe cine caută, nu în ce registru stă. Rolul decide doar unde se creează unul
 * nou: Organizator și Suport la furnizori (îi plătim), restul la clienți, fără agent.
 */
export function SelectorParteneri({
  valoare,
  onChange,
  eroare: mesajEroare,
}: {
  valoare: PartenerAles[]
  onChange: (v: PartenerAles[]) => void
  eroare?: string
}) {
  // Rolul propus: primul partener e de obicei organizatorul, următorii furnizori de suport.
  const [rol, setRol] = useState<RolPartener>(valoare.length === 0 ? 'organizator' : 'suport')
  const [dublura, setDublura] = useState<string | null>(null)
  const registru = registruPentruRol(rol)

  const adauga = (p: { id: number; nume: string; cif: string | null }) => {
    if (valoare.some((x) => x.partenerId === p.id && x.rol === rol)) {
      setDublura(`„${p.nume}” e deja în listă ca ${ETICHETE_ROL_PARTENER[rol]}.`)
      return
    }
    setDublura(null)
    onChange([...valoare, { partenerId: p.id, nume: p.nume, cif: p.cif, rol, nota: '' }])
    if (rol === 'organizator') setRol('suport')
  }

  const schimba = (i: number, p: Partial<PartenerAles>) =>
    onChange(valoare.map((x, j) => (j === i ? { ...x, ...p } : x)))

  return (
    <div className="grid gap-2">
      {valoare.length > 0 && (
        <ul className="divide-y divide-b4l-border rounded-lg border border-b4l-border">
          {valoare.map((p, i) => (
            <li
              key={`${p.partenerId}-${p.rol}-${i}`}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_140px_auto]"
            >
              <div className="grid min-w-0 gap-0.5">
                <b className="truncate font-medium text-b4l-text" title={p.nume}>
                  {p.nume}
                </b>
                {p.cif && (
                  <span className="text-mic text-b4l-text-dim">
                    {/^(tel|id)\./i.test(p.cif) ? 'persoană fizică' : `CUI ${p.cif}`}
                  </span>
                )}
                <input
                  value={p.nota}
                  onChange={(e) => schimba(i, { nota: e.target.value })}
                  maxLength={200}
                  placeholder="ce a făcut, pe scurt — ex. roll-up-uri, montaj stand"
                  aria-label={`Notă pentru ${p.nume}`}
                  className="mt-1 h-8 rounded-caseta border border-b4l-border bg-b4l-surface px-2 text-mic text-b4l-text"
                />
              </div>
              <select
                value={p.rol}
                onChange={(e) => schimba(i, { rol: e.target.value as RolPartener })}
                aria-label={`Rolul lui ${p.nume}`}
                className={`${intrare} col-start-1 row-start-2 sm:col-start-2 sm:row-start-1`}
              >
                {ROLURI_PARTENER.map((r) => (
                  <option key={r} value={r}>
                    {ETICHETE_ROL_PARTENER[r]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onChange(valoare.filter((_, j) => j !== i))}
                aria-label={`Scoate partenerul ${p.nume}`}
                className="grid h-9 w-9 place-items-center rounded-caseta border border-b4l-border text-b4l-text-muted hover:text-b4l-red-dark"
              >
                <X size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-2 rounded-lg border border-dashed border-b4l-border p-2.5">
        <div className="grid gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
            {valoare.length ? 'Mai adaugă un partener, ca' : 'Adaugă partenerul, ca'}
          </span>
          <div role="radiogroup" aria-label="Rolul partenerului" className="flex flex-wrap gap-1.5">
            {ROLURI_PARTENER.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={rol === r}
                onClick={() => {
                  setRol(r)
                  setDublura(null)
                }}
                title={DESCRIERE_ROL_PARTENER[r]}
                className={`h-8 rounded-full border px-3 text-mic ${rol === r ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark' : 'border-b4l-border bg-b4l-surface text-b4l-text'}`}
              >
                {ETICHETE_ROL_PARTENER[r]}
              </button>
            ))}
          </div>
          <span className="text-mic text-b4l-text-dim">
            {DESCRIERE_ROL_PARTENER[rol]}. Unul nou se salvează la{' '}
            {registru === 'furnizor'
              ? 'furnizori — facturile lui se leagă la Bani'
              : 'parteneri, fără agent de vânzări'}
            .
          </span>
        </div>
        {/* `key`: alt rol = alt registru pentru cei noi; câmpul pornește curat. */}
        <CautaAdaugaPartener
          key={registru}
          baza={`/campanii/registru/${registru}`}
          value={null}
          onChange={(p) => p && adauga(p)}
          rolNou={registru}
          grupuri={registru === 'client' ? GRUPURI_BENEFICIAR : undefined}
          grupImplicit={registru === 'client' ? 'partener' : undefined}
          placeholder="Caută partenerul — denumire sau cod fiscal"
        />
        {dublura && <p className="text-mic text-b4l-amber-dark">{dublura}</p>}
      </div>
      {mesajEroare && <p className="text-mic text-b4l-red">{mesajEroare}</p>}
    </div>
  )
}
